// ============= ADMIN CONTROL PANEL (Inclusion+AI v3.8) =============
// Centralized management UI for the Inclusion Head: users, children, cases, evaluations,
// certificates, activity log. Replaces the legacy pricing dashboard.
//
// All endpoints are mounted under /api/admin/* and gated by account_type === 'super_admin' (P0.1).

import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Users, Baby, ClipboardList, Award, Activity, BarChart3, Shield, Search, Trash2, Pencil, X, Save, AlertTriangle, Building2, Plus } from "lucide-react";
import { useT } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Helpers =====
const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const ROLE_BADGES = {
  inclusion_head: { ar: "رئيس دمج", en: "Inclusion Head", color: "#2F5233" },
  specialist:     { ar: "أخصائي",   en: "Specialist",     color: "#769FCD" },
  parent:         { ar: "ولي أمر",   en: "Parent",         color: "#E8A317" },
};

const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-white rounded-2xl border border-[#E5E2DB] shadow-sm p-5 ${className}`} {...rest}>{children}</div>
);

// ===== Forbidden screen =====
function Forbidden() {
  const { t } = useT();
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="admin-forbidden">
      <Card className="max-w-md text-center">
        <AlertTriangle size={42} className="mx-auto text-amber-500 mb-3"/>
        <h2 className="text-xl font-bold mb-2">403 — Forbidden</h2>
        <p className="text-sm text-[#4A5553]">{t("admin.forbidden")}</p>
      </Card>
    </div>
  );
}

// ===== KPI tile =====
function Kpi({ label, value, icon: Icon, color = "#2F5233", testid }) {
  return (
    <Card data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-[#4A5553]">{label}</p>
          <p className="text-3xl font-bold mt-1" style={{ color }}>{value ?? "—"}</p>
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15`, color }}>
          <Icon size={20}/>
        </div>
      </div>
    </Card>
  );
}

// ===== Edit user modal =====
function EditUserModal({ user, onClose, onSaved }) {
  const { t, lang } = useT();
  const [name, setName] = useState(user.name || "");
  const [accountType, setAccountType] = useState(user.account_type || "parent");
  const [role, setRole] = useState(user.role || "user");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await axios.patch(`${API}/admin/users/${user.id}`, { name, account_type: accountType, role });
      onSaved(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "save_failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[800] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="edit-user-modal">
      <button type="button" onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-label="Close"/>
      <Card className="relative w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">{t("admin.change_role")}</h3>
          <button onClick={onClose} aria-label="Close"><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-[#4A5553] mb-1">{t("admin.col_name")}</label>
            <input data-testid="edit-user-name" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-[#E5E2DB] rounded-lg text-sm"/>
          </div>
          <div>
            <label className="block text-xs text-[#4A5553] mb-1">{t("admin.col_account_type")}</label>
            <select data-testid="edit-user-account-type" value={accountType} onChange={e => setAccountType(e.target.value)} className="w-full px-3 py-2 border border-[#E5E2DB] rounded-lg text-sm bg-white">
              <option value="parent">{lang === "ar" ? "ولي أمر" : "Parent"}</option>
              <option value="specialist">{lang === "ar" ? "أخصائي" : "Specialist"}</option>
              <option value="inclusion_head">{lang === "ar" ? "رئيس قسم الدمج" : "Inclusion Head"}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-[#4A5553] mb-1">{t("admin.col_role")}</label>
            <select data-testid="edit-user-role" value={role} onChange={e => setRole(e.target.value)} className="w-full px-3 py-2 border border-[#E5E2DB] rounded-lg text-sm bg-white">
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
          {err && <p className="text-red-600 text-sm">{err}</p>}
        </div>
        <div className="flex gap-2 mt-5">
          <button data-testid="edit-user-save" onClick={save} disabled={saving} className="flex-1 bg-[#2F5233] text-white py-2 rounded-lg font-medium inline-flex items-center justify-center gap-1 disabled:opacity-60">
            <Save size={14}/> {t("common.save")}
          </button>
          <button onClick={onClose} className="px-4 py-2 border border-[#E5E2DB] rounded-lg text-sm">{t("common.cancel")}</button>
        </div>
      </Card>
    </div>
  );
}

// ===== Overview Tab =====
function OverviewTab() {
  const { t } = useT();
  const [stats, setStats] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/overview`).then(r => setStats(r.data)).catch(() => setStats({})); }, []);
  if (!stats) return <p className="text-sm text-[#4A5553]">{t("common.loading")}</p>;
  return (
    <div className="space-y-4" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="kpi-users" label={t("admin.kpi_users")} value={stats.users} icon={Users} color="#2F5233"/>
        <Kpi testid="kpi-parents" label={t("admin.kpi_parents")} value={stats.parents} icon={Users} color="#E8A317"/>
        <Kpi testid="kpi-specialists" label={t("admin.kpi_specialists")} value={stats.specialists} icon={Users} color="#769FCD"/>
        <Kpi testid="kpi-heads" label={t("admin.kpi_heads")} value={stats.heads} icon={Shield} color="#9C6644"/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="kpi-children" label={t("admin.kpi_children")} value={stats.children} icon={Baby} color="#769FCD"/>
        <Kpi testid="kpi-cases" label={t("admin.kpi_cases")} value={stats.cases} icon={ClipboardList} color="#2F5233"/>
        <Kpi testid="kpi-evaluations" label={t("admin.kpi_evaluations")} value={stats.evaluations} icon={Activity} color="#9C6644"/>
        <Kpi testid="kpi-certificates" label={t("admin.kpi_certificates")} value={stats.certificates} icon={Award} color="#E8A317"/>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Kpi testid="kpi-today" label={t("admin.kpi_today")} value={stats.signups_today} icon={BarChart3} color="#2F5233"/>
        <Kpi testid="kpi-week" label={t("admin.kpi_week")} value={stats.signups_week} icon={BarChart3} color="#769FCD"/>
      </div>
    </div>
  );
}

// ===== Users Tab =====
function UsersTab() {
  const { t, lang } = useT();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, { params: { q } });
      setUsers(data);
    } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const remove = async (u) => {
    if (!window.confirm(t("admin.confirm_delete_user"))) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      setUsers(users.filter(x => x.id !== u.id));
    } catch (e) { alert(e.response?.data?.detail || "delete_failed"); }
  };

  return (
    <div data-testid="admin-users-tab">
      <div className="relative mb-3">
        <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-[#4A5553]"/>
        <input data-testid="admin-users-search" value={q} onChange={e => setQ(e.target.value)} placeholder={t("admin.search_users")}
          className="w-full ps-9 pe-3 py-2 border border-[#E5E2DB] rounded-lg text-sm bg-white"/>
      </div>
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F8F6] text-[#4A5553]">
              <tr>
                <th className="text-start p-3">{t("admin.col_name")}</th>
                <th className="text-start p-3">{t("admin.col_email")}</th>
                <th className="text-start p-3">{t("admin.col_account_type")}</th>
                <th className="text-start p-3">{t("admin.col_role")}</th>
                <th className="text-start p-3">{t("admin.col_created")}</th>
                <th className="text-end p-3">{t("admin.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-6 text-center text-[#4A5553]">{t("common.loading")}</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-[#4A5553]">{t("admin.empty_users")}</td></tr>
              ) : users.map(u => {
                const badge = ROLE_BADGES[u.account_type] || ROLE_BADGES.parent;
                return (
                  <tr key={u.id} className="border-t border-[#E5E2DB]" data-testid={`user-row-${u.id}`}>
                    <td className="p-3 font-medium">{u.name || "—"}{u.is_demo && <span className="ms-1 text-[10px] text-[#E8A317]">• demo</span>}</td>
                    <td className="p-3 text-[#4A5553]">{u.email}</td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${badge.color}18`, color: badge.color }}>{badge[lang] || badge.ar}</span></td>
                    <td className="p-3 text-[#4A5553]">{u.role || "user"}</td>
                    <td className="p-3 text-[#4A5553]">{fmtDate(u.created_at, lang)}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`edit-user-${u.id}`} onClick={() => setEditing(u)} className="p-1.5 rounded hover:bg-[#F0EFEB] text-[#2F5233]" aria-label="Edit"><Pencil size={14}/></button>
                        <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} className="p-1.5 rounded hover:bg-red-50 text-red-600" aria-label="Delete"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={u => { setUsers(users.map(x => x.id === u.id ? u : x)); setEditing(null); }}/>}
    </div>
  );
}

// ===== Generic list tab (for Children / Cases / Evaluations / Certificates / Activity) =====
function ListTab({ endpoint, columns, emptyKey, testid }) {
  const { t, lang } = useT();
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/${endpoint}`).then(r => setRows(r.data)).catch(() => setRows([])); }, [endpoint]);
  if (rows === null) return <p className="text-sm text-[#4A5553]">{t("common.loading")}</p>;
  return (
    <Card className="p-0 overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6] text-[#4A5553]">
            <tr>{columns.map(c => <th key={c.key} className="text-start p-3">{t(c.label)}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="p-6 text-center text-[#4A5553]">{t(emptyKey)}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.id || i} className="border-t border-[#E5E2DB]" data-testid={`${testid}-row-${i}`}>
                {columns.map(c => <td key={c.key} className="p-3">{c.render ? c.render(r, lang) : (r[c.key] || "—")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ===== Schools management (P0.3 CRUD → Platform Super Admin UI) =====
const SCHOOL_TYPES = ["public", "private", "center", "international", "other"];
const SCHOOL_STATUSES = ["active", "suspended", "pending"];
const TYPE_LABEL = {
  public: { ar: "حكومية", en: "Public" }, private: { ar: "خاصة", en: "Private" },
  center: { ar: "مركز", en: "Center" }, international: { ar: "دولية", en: "International" },
  other: { ar: "أخرى", en: "Other" },
};
const STATUS_LABEL = {
  active: { ar: "نشطة", en: "Active", color: "#2F5233" },
  suspended: { ar: "موقوفة", en: "Suspended", color: "#B4413C" },
  pending: { ar: "قيد الاعتماد", en: "Pending", color: "#E8A317" },
};

function SchoolFormModal({ school, onClose, onSaved }) {
  const { lang } = useT();
  const isEdit = !!school;
  const [f, setF] = useState({
    name: school?.name || "", code: school?.code || "", country: school?.country || "",
    emirate: school?.emirate || "", type: school?.type || "private", status: school?.status || "pending",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.name.trim()) { setErr(lang === "ar" ? "اسم المدرسة مطلوب" : "School name is required"); return; }
    setSaving(true); setErr("");
    try {
      if (isEdit) await axios.patch(`${API}/schools/${school.id}`, f);
      else await axios.post(`${API}/schools`, f);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || (lang === "ar" ? "حدث خطأ" : "Error")); }
    finally { setSaving(false); }
  };
  const Lbl = ({ children }) => <label className="block text-xs text-[#4A5553] mb-1">{children}</label>;
  const inputCls = "w-full border border-[#E5E2DB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2F5233]";
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="school-form-modal">
      <Card className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#2F5233]">
            {isEdit ? (lang === "ar" ? "تعديل مدرسة" : "Edit School") : (lang === "ar" ? "إضافة مدرسة" : "Add School")}
          </h3>
          <button onClick={onClose} data-testid="school-form-close" className="text-[#4A5553] hover:text-black"><X size={18}/></button>
        </div>
        {err && <div className="mb-3 text-sm text-[#B4413C] bg-[#B4413C]/10 rounded-lg px-3 py-2" data-testid="school-form-error">{err}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Lbl>{lang === "ar" ? "اسم المدرسة" : "School name"}</Lbl>
            <input className={inputCls} value={f.name} onChange={set("name")} data-testid="school-form-name"/>
          </div>
          <div>
            <Lbl>{lang === "ar" ? "الكود (اختياري)" : "Code (optional)"}</Lbl>
            <input className={inputCls} value={f.code} onChange={set("code")} placeholder="SCH-XXXXXX" data-testid="school-form-code"/>
          </div>
          <div>
            <Lbl>{lang === "ar" ? "الدولة" : "Country"}</Lbl>
            <input className={inputCls} value={f.country} onChange={set("country")} data-testid="school-form-country"/>
          </div>
          <div>
            <Lbl>{lang === "ar" ? "الإمارة / المنطقة" : "Emirate / Region"}</Lbl>
            <input className={inputCls} value={f.emirate} onChange={set("emirate")} data-testid="school-form-emirate"/>
          </div>
          <div>
            <Lbl>{lang === "ar" ? "النوع" : "Type"}</Lbl>
            <select className={inputCls} value={f.type} onChange={set("type")} data-testid="school-form-type">
              {SCHOOL_TYPES.map(tt => <option key={tt} value={tt}>{TYPE_LABEL[tt][lang === "ar" ? "ar" : "en"]}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <Lbl>{lang === "ar" ? "الحالة" : "Status"}</Lbl>
            <select className={inputCls} value={f.status} onChange={set("status")} data-testid="school-form-status">
              {SCHOOL_STATUSES.map(ss => <option key={ss} value={ss}>{STATUS_LABEL[ss][lang === "ar" ? "ar" : "en"]}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#E5E2DB] text-sm">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
          <button onClick={submit} disabled={saving} data-testid="school-form-submit"
            className="px-4 py-2 rounded-lg bg-[#2F5233] text-white text-sm inline-flex items-center gap-2 disabled:opacity-60">
            <Save size={14}/> {saving ? (lang === "ar" ? "جارٍ الحفظ…" : "Saving…") : (lang === "ar" ? "حفظ" : "Save")}
          </button>
        </div>
      </Card>
    </div>
  );
}

function SchoolsTab() {
  const { lang } = useT();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);       // {} = create, school obj = edit
  const [confirmDel, setConfirmDel] = useState(null);
  const load = useCallback(async () => {
    setRows(null);
    try { const { data } = await axios.get(`${API}/schools`, { params: q ? { q } : {} }); setRows(data); }
    catch { setRows([]); }
  }, [q]);
  useEffect(() => { load(); }, [load]);
  const doDelete = async (s) => { try { await axios.delete(`${API}/schools/${s.id}`); } finally { setConfirmDel(null); load(); } };

  return (
    <div className="space-y-4" data-testid="schools-tab">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute top-1/2 -translate-y-1/2 start-3 text-[#4A5553]"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="schools-search"
            placeholder={lang === "ar" ? "بحث بالاسم أو الكود…" : "Search by name or code…"}
            className="w-full ps-9 pe-3 py-2 rounded-lg border border-[#E5E2DB] text-sm focus:outline-none focus:border-[#2F5233]"/>
        </div>
        <button onClick={() => setModal({})} data-testid="school-add-btn"
          className="px-4 py-2 rounded-lg bg-[#2F5233] text-white text-sm inline-flex items-center gap-2">
          <Plus size={15}/> {lang === "ar" ? "إضافة مدرسة" : "Add School"}
        </button>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F0EFEB] text-[#4A5553]">
            <tr>
              <th className="text-start p-3">{lang === "ar" ? "المدرسة" : "School"}</th>
              <th className="text-start p-3">{lang === "ar" ? "الكود" : "Code"}</th>
              <th className="text-start p-3">{lang === "ar" ? "النوع" : "Type"}</th>
              <th className="text-start p-3">{lang === "ar" ? "الموقع" : "Location"}</th>
              <th className="text-start p-3">{lang === "ar" ? "الحالة" : "Status"}</th>
              <th className="text-end p-3">{lang === "ar" ? "إجراءات" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={6} className="p-6 text-center text-[#4A5553]">{lang === "ar" ? "جارٍ التحميل…" : "Loading…"}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-[#4A5553]" data-testid="schools-empty">
                {lang === "ar" ? "لا توجد مدارس بعد. أضف أول مدرسة." : "No schools yet. Add your first school."}
              </td></tr>
            ) : rows.map((s, i) => {
              const st = STATUS_LABEL[s.status] || STATUS_LABEL.pending;
              return (
                <tr key={s.id} className="border-t border-[#E5E2DB]" data-testid={`school-row-${i}`}>
                  <td className="p-3 font-medium">{s.name}</td>
                  <td className="p-3"><code className="text-xs bg-[#F0EFEB] px-1.5 py-0.5 rounded">{s.code}</code></td>
                  <td className="p-3">{(TYPE_LABEL[s.type] || TYPE_LABEL.other)[lang === "ar" ? "ar" : "en"]}</td>
                  <td className="p-3 text-[#4A5553]">{[s.emirate, s.country].filter(Boolean).join("، ") || "—"}</td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ background: `${st.color}18`, color: st.color }} data-testid={`school-status-${i}`}>
                      {st[lang === "ar" ? "ar" : "en"]}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setModal(s)} data-testid={`school-edit-${i}`}
                        className="p-1.5 rounded-lg hover:bg-[#F0EFEB] text-[#2F5233]"><Pencil size={15}/></button>
                      <button onClick={() => setConfirmDel(s)} data-testid={`school-delete-${i}`}
                        className="p-1.5 rounded-lg hover:bg-[#B4413C]/10 text-[#B4413C]"><Trash2 size={15}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {modal !== null && (
        <SchoolFormModal school={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>
      )}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="school-delete-modal">
          <Card className="max-w-sm text-center">
            <AlertTriangle size={36} className="mx-auto text-[#B4413C] mb-2"/>
            <p className="text-sm mb-4">
              {lang === "ar" ? `حذف مدرسة "${confirmDel.name}"؟ لا يمكن التراجع.` : `Delete school "${confirmDel.name}"? This cannot be undone.`}
            </p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 rounded-lg border border-[#E5E2DB] text-sm">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
              <button onClick={() => doDelete(confirmDel)} data-testid="school-delete-confirm"
                className="px-4 py-2 rounded-lg bg-[#B4413C] text-white text-sm">{lang === "ar" ? "حذف" : "Delete"}</button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ===== Main AdminPanel =====
export function AdminPanel() {
  const { t, lang } = useT();
  // Read current user (cached on /auth/me) to gate this page
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  useEffect(() => { axios.get(`${API}/auth/me`).then(r => setMe(r.data)).catch(() => setMe({})); }, []);

  if (me === null) return <p className="p-6 text-sm text-[#4A5553]">{t("common.loading")}</p>;
  if (me.account_type !== "super_admin") return <Forbidden/>;

  const tabs = [
    { key: "overview",     label: t("admin.tab_overview"),     icon: BarChart3 },
    { key: "schools",      label: lang === "ar" ? "المدارس" : "Schools", icon: Building2 },
    { key: "users",        label: t("admin.tab_users"),        icon: Users },
    { key: "children",     label: t("admin.tab_children"),     icon: Baby },
    { key: "cases",        label: t("admin.tab_cases"),        icon: ClipboardList },
    { key: "evaluations",  label: t("admin.tab_evaluations"),  icon: Activity },
    { key: "certificates", label: t("admin.tab_certificates"), icon: Award },
    { key: "activity",     label: t("admin.tab_activity"),     icon: Shield },
  ];

  return (
    <div className="space-y-5 fade-up" data-testid="admin-panel">
      <Card className="bg-gradient-to-l from-[#2F5233]/8 to-transparent border-[#2F5233]/20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#2F5233] text-white flex items-center justify-center"><Shield size={24}/></div>
          <div>
            <h1 className="text-2xl font-bold text-[#2F5233] font-heading">{t("admin.title")}</h1>
            <p className="text-sm text-[#4A5553]">{t("admin.subtitle")}</p>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" data-testid="admin-tabs">
        {tabs.map(tb => {
          const Icon = tb.icon;
          const active = tab === tb.key;
          return (
            <button key={tb.key} onClick={() => setTab(tb.key)} role="tab" aria-selected={active}
              data-testid={`admin-tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition border ${active ? "bg-[#2F5233] text-white border-[#2F5233]" : "bg-white text-[#4A5553] border-[#E5E2DB] hover:border-[#2F5233]"}`}>
              <Icon size={14}/> {tb.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "overview" && <OverviewTab/>}
      {tab === "schools" && <SchoolsTab/>}
      {tab === "users" && <UsersTab/>}
      {tab === "children" && <ListTab endpoint="children" testid="tbl-children" emptyKey="admin.empty_children"
        columns={[
          { key: "name", label: "admin.col_name" },
          { key: "age", label: "common.name", render: (r) => r.age ? `${r.age}` : "—" },
          { key: "access_code", label: "admin.col_status", render: (r) => <code className="text-xs bg-[#F0EFEB] px-1.5 py-0.5 rounded">{r.access_code || "—"}</code> },
          { key: "created_at", label: "admin.col_created", render: (r, l) => fmtDate(r.created_at, l) },
        ]}/>}
      {tab === "cases" && <ListTab endpoint="cases" testid="tbl-cases" emptyKey="admin.empty_cases"
        columns={[
          { key: "child_name", label: "admin.col_child", render: (r) => r.child_name || r.title || "—" },
          { key: "primary_diagnosis", label: "admin.col_status", render: (r) => r.primary_diagnosis || r.status || "—" },
          { key: "created_at", label: "admin.col_date", render: (r, l) => fmtDate(r.created_at, l) },
        ]}/>}
      {tab === "evaluations" && <ListTab endpoint="evaluations" testid="tbl-evaluations" emptyKey="admin.empty_evaluations"
        columns={[
          { key: "title", label: "admin.col_name", render: (r) => r.title || r.eval_title || "—" },
          { key: "domain", label: "admin.col_status" },
          { key: "achievement_score", label: "admin.col_score", render: (r) => r.achievement_score != null ? `${r.achievement_score}%` : "—" },
          { key: "created_at", label: "admin.col_date", render: (r, l) => fmtDate(r.created_at, l) },
        ]}/>}
      {tab === "certificates" && <ListTab endpoint="certificates" testid="tbl-certificates" emptyKey="admin.empty_certificates"
        columns={[
          { key: "child_name", label: "admin.col_child" },
          { key: "goal_title", label: "admin.col_name" },
          { key: "mastery_score", label: "admin.col_score", render: (r) => `${r.mastery_score ?? r.success_rate ?? 0}%` },
          { key: "issued_at", label: "admin.col_date", render: (r, l) => fmtDate(r.issued_at, l) },
        ]}/>}
      {tab === "activity" && <ListTab endpoint="activity" testid="tbl-activity" emptyKey="admin.empty_activity"
        columns={[
          { key: "action", label: "admin.col_action" },
          { key: "actor_name", label: "admin.col_actor", render: (r) => r.actor_name || r.user_email || r.user_id || "—" },
          { key: "case_id", label: "admin.col_status", render: (r) => r.case_id ? <code className="text-[10px]">{(r.case_id || "").slice(0,8)}…</code> : "—" },
          { key: "ts", label: "admin.col_date", render: (r, l) => fmtDate(r.ts, l) },
        ]}/>}
    </div>
  );
}
